export interface ActionTrailEvent {
  id: string;
  ts: number;
  type: string;
  name: string;
  url?: string;
  data?: Record<string, unknown>;
}

export interface ActionTrailRecordInput {
  type: string;
  name: string;
  data?: Record<string, unknown>;
  /**
   * Optional URL override. If omitted, we attempt to read from location.
   * Keep this PII-safe (prefer pathname + search; avoid full origins if not needed).
   */
  url?: string;
}

const ACTION_TRAIL_KEY = 'inovisum:action-trail:v1';
const MAX_EVENTS = 200;

function safeNow(): number {
  return Date.now();
}

function safeId(): string {
  try {
    const anyCrypto = (globalThis as any).crypto as Crypto | undefined;
    if (anyCrypto?.randomUUID) return anyCrypto.randomUUID();
  } catch {
    // ignore; fallback below
  }
  return `${safeNow()}-${Math.random().toString(16).slice(2)}`;
}

function safeUrl(): string | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    const loc = window.location;
    if (!loc) return undefined;
    const path = loc.pathname || '';
    const search = loc.search || '';
    const hash = loc.hash || '';
    return `${path}${search}${hash}`;
  } catch {
    return undefined;
  }
}

function readStored(): ActionTrailEvent[] {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.sessionStorage?.getItem(ACTION_TRAIL_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e: any) => e && typeof e === 'object')
      .slice(-MAX_EVENTS)
      .map((e: any): ActionTrailEvent => ({
        id: typeof e.id === 'string' ? e.id : safeId(),
        ts: typeof e.ts === 'number' ? e.ts : safeNow(),
        type: typeof e.type === 'string' ? e.type : 'unknown',
        name: typeof e.name === 'string' ? e.name : 'unknown',
        url: typeof e.url === 'string' ? e.url : undefined,
        data: e.data && typeof e.data === 'object' ? e.data : undefined,
      }));
  } catch (error) {
    // Session storage may throw (quota, disabled). Trail should remain best-effort.
    // eslint-disable-next-line no-console
    console.warn('[telemetry][trail] failed to read sessionStorage', error);
    return [];
  }
}

function writeStored(events: ActionTrailEvent[]): void {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage?.setItem(ACTION_TRAIL_KEY, JSON.stringify(events.slice(-MAX_EVENTS)));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[telemetry][trail] failed to write sessionStorage', error);
  }
}

function getMemoryStore(): ActionTrailEvent[] {
  const g: any = globalThis as any;
  if (!g.__INOVISUM_ACTION_TRAIL__) {
    g.__INOVISUM_ACTION_TRAIL__ = readStored();
  }
  return g.__INOVISUM_ACTION_TRAIL__ as ActionTrailEvent[];
}

export function recordActionTrailEvent(input: ActionTrailRecordInput): ActionTrailEvent {
  const store = getMemoryStore();
  const evt: ActionTrailEvent = {
    id: safeId(),
    ts: safeNow(),
    type: input.type,
    name: input.name,
    url: input.url ?? safeUrl(),
    data: input.data,
  };

  store.push(evt);
  if (store.length > MAX_EVENTS) {
    store.splice(0, store.length - MAX_EVENTS);
  }
  writeStored(store);
  return evt;
}

export function getActionTrailSnapshot(): ActionTrailEvent[] {
  const store = getMemoryStore();
  return store.slice();
}

export function clearActionTrail(): void {
  const store = getMemoryStore();
  store.splice(0, store.length);
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage?.removeItem(ACTION_TRAIL_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[telemetry][trail] failed to clear sessionStorage', error);
  }
}

